import React, { CSSProperties, useMemo, useState } from 'react';
import { Alert, Card, Radio, Switch, Typography } from '@duxton-web/ui';
import { styled } from 'styled-components';

import {
  useVariableContext,
  useVariableDispatchContext,
} from '../../contexts/variable-context';
import {
  variableDetailUpdate,
  variableJsonSchemaUpdate,
} from '../../store/actions/variable/action-creators';
import { isFieldChanged } from '../../utils';
import Contextual from './contextual';
import JsonSchema from './json-schema';
import RangeMultipleContent, {
  RangeMultipleValidationType,
} from './range-multiple-content';
import { VariableType } from '@/constants';
import {
  SyncedFromGrabOverlay,
  useIsSyncedFromGrab,
} from '@/features/tenant-sync';
import Service from '@/lib/App';
import { VariableInfo } from '@/lib/PlannerDTOs';
import { parse } from '@/utils/json';

const wildcard = '*';

enum ValidationType {
  range = 'Range',
  multiple = 'Multiple',
  contextual = 'Contextual',
  pattern = 'Pattern',
}

enum JsonValidationType {
  json = 'JSON',
  contextual = 'Contextual',
}

const ValidationTypeSupportedTypes = {
  [ValidationType.range]: {
    supportedTypes: ['string', 'int64', 'float64'],
  },
  [ValidationType.multiple]: {
    supportedTypes: wildcard,
  },
  [ValidationType.contextual]: {
    supportedTypes: wildcard,
  },
  // [ValidationType.pattern]: {
  //   supportedTypes: ['string'],
  // },
};

export enum SchemaType {
  STRING = 'string',
  NUMBER = 'number',
  INTEGER = 'integer',
  OBJECT = 'object',
  ARRAY = 'array',
}

export class JSONSchema {
  $schema?: string;
  type: SchemaType;
  minLength?: number;
  maxLength?: number;
  format?: string;
  pattern?: string;
  minimum?: number;
  maximum?: number;
  multipleOf?: number;
  enum?: string[] | number[];
}

const getValidationTypeForVariableType = (type: string) => {
  const supportedValidationTypeOptions = Object.keys(
    ValidationTypeSupportedTypes,
  ).filter((key) => {
    const supportedTypes = ValidationTypeSupportedTypes[key].supportedTypes;

    return supportedTypes.includes(type) || supportedTypes === wildcard;
  });

  return supportedValidationTypeOptions.map((option) => {
    return {
      label: option,
      value: option,
    };
  });
};

export const isEmpty = (_variable: VariableInfo, _value: any): boolean => {
  return false;
};

export const getSchemaTypeByVariableType = (variable: VariableInfo): string => {
  switch (variable?.type) {
    case VariableType.STRING:
    case VariableType.FILE:
      return SchemaType.STRING;
    case VariableType.INT64:
      return SchemaType.INTEGER;
    case VariableType.FLOAT64:
      return SchemaType.NUMBER;
    case VariableType.JSON:
    case VariableType.MAP:
      return SchemaType.OBJECT;
    default:
      return null;
  }
};

interface ValidationTypeContentProps {
  validationType: ValidationType;
  onValidationTypeChange: (type: ValidationType) => void;
  disabled?: boolean;
}

const ValidationTypeContent = ({
  validationType,
  onValidationTypeChange,
  disabled,
}: ValidationTypeContentProps) => {
  if (validationType === ValidationType.contextual) return <Contextual />;
  return (
    <RangeMultipleContent
      showSelector={false}
      value={validationType as unknown as RangeMultipleValidationType}
      onChange={(nextType) =>
        onValidationTypeChange(nextType as unknown as ValidationType)
      }
      disabled={disabled}
    />
  );
};

interface ValidationSetupWrapperProps {
  enable: boolean;
}

const ValidationSetupWrapper = ({ enable }: ValidationSetupWrapperProps) => {
  if (!enable) return null;

  const {
    variable,
    disabled: contextDisabled,
    variableDiff,
    variableIsValid,
  } = useVariableContext();
  const dispatch = useVariableDispatchContext();

  // Check if variable is synced from Grab (tenant portal)
  const isSyncedFromGrab = useIsSyncedFromGrab(variable);
  const disabled = contextDisabled || isSyncedFromGrab;

  const isJsonType = variable?.type === VariableType.JSON;
  const isMapType = variable?.type === VariableType.MAP;

  const [jsonValidationType, updateJsonValidationType] =
    useState<JsonValidationType>(() => {
      const contextualRules = variable?.metadata?.contextualRules;
      const hasContextualRules = Array.isArray(contextualRules)
        ? contextualRules.length > 0
        : false;
      return hasContextualRules
        ? JsonValidationType.contextual
        : JsonValidationType.json;
    });

  const [validationType, updateValidationType] = useState(() => {
    const jsonSchema = variable?.metadata?.jsonSchema;
    const contextualRules = variable?.metadata?.contextualRules;
    const hasContextualRules = Array.isArray(contextualRules)
      ? contextualRules.length > 0
      : false;

    if (hasContextualRules) {
      return ValidationType.contextual;
    }

    if (jsonSchema) {
      try {
        const parsedJsonSchema = parse(jsonSchema);
        return parsedJsonSchema?.enum?.length
          ? ValidationType.multiple
          : ValidationType.range;
      } catch (error) {
        console.error('Error parsing JSON schema:', error);
        return ValidationType.range;
      }
    }

    return getValidationTypeForVariableType(variable?.type)[0].value;
  });

  if (isMapType) return <JsonSchema />;

  if (isJsonType) {
    const jsonValidationTypeOptions = [
      { label: JsonValidationType.json, value: JsonValidationType.json },
      {
        label: JsonValidationType.contextual,
        value: JsonValidationType.contextual,
      },
    ];

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <SecondaryText>Validation Type</SecondaryText>
        <Radio.Group
          id="validation-type"
          value={jsonValidationType}
          options={jsonValidationTypeOptions}
          optionType="button"
          disabled={disabled}
          onChange={(e) => {
            const nextType = e.target.value as JsonValidationType;
            updateJsonValidationType(nextType);

            if (nextType === JsonValidationType.contextual) {
              dispatch(variableJsonSchemaUpdate(null, true));
              if (!variable?.metadata?.contextualRules?.length) {
                dispatch(
                  variableDetailUpdate(
                    {
                      ...variable,
                      metadata: {
                        ...variable?.metadata,
                        contextualRules: [{ contextualRulesConditions: [] }],
                      },
                    } as VariableInfo,
                    variableIsValid,
                  ),
                );
              }
            } else {
              dispatch(
                variableJsonSchemaUpdate(
                  { type: getSchemaTypeByVariableType(variable) } as JSONSchema,
                  false,
                ),
              );
            }
          }}
        />
        {jsonValidationType === JsonValidationType.contextual ? (
          <Contextual />
        ) : (
          <JsonSchema />
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <SecondaryText>Validation Type</SecondaryText>
      <Radio.Group
        id="validation-type"
        className={`${
          isFieldChanged(variableDiff, 'metadata/jsonSchema')
            ? 'changelog-item-variable-modified'
            : ''
        }`}
        value={validationType}
        options={getValidationTypeForVariableType(variable?.type)}
        optionType="button"
        disabled={disabled}
        onChange={(e) => {
          const nextType = e.target.value;
          updateValidationType(nextType);

          if (nextType === ValidationType.contextual) {
            dispatch(variableJsonSchemaUpdate(null, true));
            if (!variable?.metadata?.contextualRules?.length) {
              dispatch(
                variableDetailUpdate(
                  {
                    ...variable,
                    metadata: {
                      ...variable?.metadata,
                      contextualRules: [{ contextualRulesConditions: [] }],
                    },
                  } as VariableInfo,
                  variableIsValid,
                ),
              );
            }
          } else {
            dispatch(
              variableJsonSchemaUpdate(
                {
                  type: getSchemaTypeByVariableType(variable),
                } as JSONSchema,
                false,
              ),
            );
          }
        }}
      />
      <ValidationTypeContent
        validationType={validationType as ValidationType}
        onValidationTypeChange={(nextType) => updateValidationType(nextType)}
        disabled={disabled}
      />
    </div>
  );
};

interface ValidationProps {
  style?: CSSProperties;
}

const Validation = ({ style }: ValidationProps) => {
  const {
    variable,
    disabled: contextDisabled,
    previousVariable,
  } = useVariableContext();
  const dispatch = useVariableDispatchContext();

  // Check if variable is synced from Grab (tenant portal)
  const isSyncedFromGrab = useIsSyncedFromGrab(variable);
  const disabled = contextDisabled || isSyncedFromGrab;

  const supportedValidationDataTypes = useMemo(() => {
    return Service.cache!.getAllVariableTypes().filter((val) => {
      return val !== VariableType.BOOLEAN;
    });
  }, [variable]);

  if (
    previousVariable &&
    !previousVariable?.metadata?.jsonSchema &&
    !variable?.metadata?.jsonSchema
  )
    return <div />;

  if (
    variable?.type &&
    (!supportedValidationDataTypes.includes(variable?.type) ||
      variable?.type === VariableType.BOOLEAN)
  ) {
    if (!previousVariable)
      return (
        <Alert
          style={style}
          message={
            <Typography.Text>
              Validation is not supported for{' '}
              <Typography.Text strong>{variable?.type}</Typography.Text>
            </Typography.Text>
          }
          type="info"
        />
      );

    return <div />;
  }

  const isValidationEnabled =
    !!variable?.metadata?.jsonSchema ||
    (variable?.metadata?.contextualRules?.length ?? 0) > 0;

  return (
    <Card style={style}>
      <div id="validation-container" style={{ position: 'relative' }}>
        <SyncedFromGrabOverlay show={isSyncedFromGrab} />
        <EnableValidationContainer>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Switch
                size="small"
                data-testid="validation-enable"
                checked={isValidationEnabled}
                disabled={disabled}
                onChange={(val) => {
                  if (!val) {
                    dispatch(
                      variableDetailUpdate(
                        {
                          ...variable,
                          metadata: {
                            ...variable?.metadata,
                            contextualRules: [],
                          },
                        } as VariableInfo,
                        true,
                      ),
                    );
                  }
                  dispatch(
                    variableJsonSchemaUpdate(
                      val ? ({} as JSONSchema) : null,
                      !val,
                    ),
                  );
                }}
              />
            </div>

            <Typography.Text>Enable Validation</Typography.Text>
          </div>

          <div style={{ display: 'flex', gap: 4 }}>
            <SecondaryText>
              This validation will guide the variable value in rollouts and
              experiments. Learn more about validation types.
            </SecondaryText>
          </div>
        </EnableValidationContainer>
        <ValidationSetupWrapper enable={isValidationEnabled} />
      </div>
    </Card>
  );
};

const EnableValidationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 16px;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
`;

const SecondaryText = styled(Typography.Metadata)`
  color: #a3a3a3;
`;

export default Validation;
